import type {
  Money,
  ConnectionType,
  Coordinate,
  IngredientId,
} from '@libs/trip-planner-interfaces';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { MyPositionType } from '@libs/trip-planner-interfaces';

export interface Rectangle {
  topLeft: Coordinate;
  bottomRight: Coordinate;
}

export type PlaceId = string;

export interface Location {
  formattedAddress: string;
  position: Coordinate;
  route?: string;
  streetNumber?: string;
  locality?: string;
  subLocality?: string;
  postalTown?: string;
  country?: string;
  postalCode?: string;
  markerFormatter?: string;
}

export interface LocationCriteria {
  position?: Coordinate;
  place?: PlaceId;
  language?: string;
}

export const enum MapboxMarkerType {
  HotelSelection = 'hotel-selection-marker',
  ActivitySelection = 'activity-selection-marker',
  StartPosition = 'start-position-marker',
  EndPosition = 'end-position-marker',
  Destination = 'destination-marker',
}

interface MapboxMarkerBase {
  key: string;
  position: Coordinate;
}

// NOTE: Used in car trip map for user's starting position
export interface StartPositionMarker extends MapboxMarkerBase {
  type: MapboxMarkerType.StartPosition;
  connectionToBeReached: ConnectionType;
  positionType: MyPositionType.Start;
}

// NOTE: Used in car trip map for user's ending position
export interface EndPositionMarker extends MapboxMarkerBase {
  type: MapboxMarkerType.EndPosition;
  connectionToBeReached: ConnectionType;
  positionType: MyPositionType.End;
}

// NOTE: Used as car trip destination and to calculate distance/time for transport in overview
export interface DestinationMarker extends MapboxMarkerBase {
  type: MapboxMarkerType.Destination;
  ingredientId: IngredientId;
  name: string;
}

// NOTE: Used in hotel selection map to display available hotels with prices
export interface HotelSelectionMarker extends MapboxMarkerBase {
  price?: Money;
  type: MapboxMarkerType.HotelSelection;
  hotelCode: string;
  ingredientId: IngredientId;
  formattedPrice: string;
}

// NOTE: Used in activity selection map to display available activities with prices
export interface ActivitySelectionMarker extends MapboxMarkerBase {
  type: MapboxMarkerType.ActivitySelection;
  activityCode: string;
  ingredientId: IngredientId;
  formattedPrice: string;
}

export type PositionMarker = StartPositionMarker | EndPositionMarker;

export type MapboxMarker =
  | StartPositionMarker
  | EndPositionMarker
  | DestinationMarker
  | HotelSelectionMarker
  | ActivitySelectionMarker;

export function isStartPositionMarker(
  marker: MapboxMarker
): marker is StartPositionMarker {
  return marker.type === MapboxMarkerType.StartPosition;
}

export function isEndPositionMarker(
  marker: MapboxMarker
): marker is EndPositionMarker {
  return marker.type === MapboxMarkerType.EndPosition;
}

export function isPositionMarker(
  marker: MapboxMarker
): marker is PositionMarker {
  return isStartPositionMarker(marker) || isEndPositionMarker(marker);
}

export function isDestinationMarker(
  marker: MapboxMarker
): marker is DestinationMarker {
  return marker.type === MapboxMarkerType.Destination;
}

export function isHotelSelectionMarker(
  marker: MapboxMarker
): marker is HotelSelectionMarker {
  return marker.type === MapboxMarkerType.HotelSelection;
}

export function isActivitySelectionMarker(
  marker: MapboxMarker
): marker is ActivitySelectionMarker {
  return marker.type === MapboxMarkerType.ActivitySelection;
}

export interface RouteConnection {
  from: MapboxMarker;
  to: MapboxMarker;
  type: ConnectionType;
}

export type Route = RouteConnection[];

export interface RouteGeometry {
  coordinates: [number, number][];
  type: string;
}

export interface RouteConnectionWithDetails extends RouteConnection {
  expectedTravelTimeInMinutes: number | undefined;
  distanceInKilometers: number | undefined;
  geometry: RouteGeometry;
}

export type RouteWithDetails = RouteConnectionWithDetails[];

export interface RouteSegmentByConnectionType {
  segment: Route;
  type: ConnectionType;
}

export interface RouteSegmentBoundaries {
  from: MapboxMarker;
  to: MapboxMarker;
  waypoints: MapboxMarker[];
  type: ConnectionType;
}
